import type { DataSource, SiteRagReport } from '../../types/siteRag';
import { buildExecutiveSummarySourceDigest } from '../buildExecutiveSummarySourceDigest';
import { hashCanonicalJson } from './reportContractHash';
import { dataCenterFinancialSectionText } from '../reportRendering/dataCenterFinancialProjection';

export const CUSTOM_BRIEF_SOURCE_FRAME_FIELD = 'custom-brief-report-source-frame';
export const CUSTOM_BRIEF_SOURCE_FRAME_VERSION = 'custom-brief-report-source-frame-v1';
export interface CustomBriefReportSourceReceipt {
  reportId: string; reportType: string; siteId: string;
  sourcePath: string; sourceHash: string; contentHash: string;
}
export interface CustomBriefReportSourceFrame {
  schemaVersion: typeof CUSTOM_BRIEF_SOURCE_FRAME_VERSION;
  siteId: string;
  sources: CustomBriefReportSourceReceipt[];
}

export function customBriefReportHasSourceBody(report: SiteRagReport): boolean {
  if (report.qualityHold === 'needs-revision' || report._metadataOnly) return false;
  const hasText = (value: unknown): boolean => typeof value === 'string'
    ? value.trim() !== '' : Boolean(value && typeof value === 'object'
      && Object.values(value).some(hasText));
  return [report.summary, report.executiveSummary, report.conclusion].some(hasText);
}

export function customBriefSourceHasImmutableRevision(report: SiteRagReport): boolean {
  const revision = report.reportRevision;
  return typeof revision?.sourcePath === 'string' && revision.sourcePath.length > 0
    && typeof revision.sourceHash === 'string' && /^[a-f0-9]{64}$/.test(revision.sourceHash);
}

/** A current semantic pointer for a type cannot certify another saved body. */
export function customBriefSourceMatchesCurrentPlan(report: SiteRagReport,
  item: { analysisId?: string; analysisInputHash?: string; analysisContentHash?: string; analysisSnapshotPath?: string },
  analysisGateEnabled?: boolean): boolean {
  if (analysisGateEnabled !== true) return true;
  const ref = report.analysisRevision;
  return Boolean(ref && ref.analysisId === item.analysisId && ref.inputHash === item.analysisInputHash
    && ref.contentHash === item.analysisContentHash && ref.snapshotPath === item.analysisSnapshotPath);
}

/** Hash only the actual synthesis projection and its evidence, not transient
 * readiness/viewer overlays. The immutable body path/hash is bound separately. */
export function customBriefReportSourceReceipt(report: SiteRagReport): CustomBriefReportSourceReceipt {
  return { reportId: report.id, reportType: report.reportType ?? '', siteId: report.siteId ?? '',
    sourcePath: report.reportRevision?.sourcePath ?? '', sourceHash: report.reportRevision?.sourceHash ?? '',
    contentHash: hashCanonicalJson({ entry: buildExecutiveSummarySourceDigest([report]).entries[0], references: report.references,
      dataCenterFinancialSummary: report.financialAnalysis ? dataCenterFinancialSectionText(report.financialAnalysis, 'financialSummary') : null,
      dataCenterCommercialBasis: report.financialAnalysis ? dataCenterFinancialSectionText(report.financialAnalysis, 'taxAndCommercial') : null }) };
}

export function customBriefSourceFrameReference(siteId: string, reports: readonly SiteRagReport[], now: string): DataSource {
  const frame: CustomBriefReportSourceFrame = { schemaVersion: CUSTOM_BRIEF_SOURCE_FRAME_VERSION, siteId,
    sources: reports.map(customBriefReportSourceReceipt).sort((a, b) => a.reportId.localeCompare(b.reportId)) };
  return { field: CUSTOM_BRIEF_SOURCE_FRAME_FIELD, source: 'Custom Brief generated-report source frame (provenance only)',
    retrievedAt: now, sourceGrade: 'inference', qualification: 'unknown',
    subItems: [{ topic: 'Source frame', detail: JSON.stringify(frame) }] };
}

export function customBriefSourceFrame(report: SiteRagReport): CustomBriefReportSourceFrame | null | 'invalid' {
  const rows = (report.references ?? []).filter(ref => ref.field === CUSTOM_BRIEF_SOURCE_FRAME_FIELD);
  if (!rows.length) return null;
  if (rows.length !== 1) return 'invalid';
  try {
    const value = JSON.parse(rows[0]!.subItems?.find(item => item.topic === 'Source frame')?.detail ?? '') as CustomBriefReportSourceFrame;
    if (value.schemaVersion !== CUSTOM_BRIEF_SOURCE_FRAME_VERSION || !value.siteId || !Array.isArray(value.sources)
      || value.sources.some(source => !source.reportId || !source.reportType || source.siteId !== value.siteId
        || !source.sourcePath || !/^[a-f0-9]{64}$/.test(source.sourceHash) || !/^[a-f0-9]{64}$/.test(source.contentHash))
      || new Set(value.sources.map(source => source.reportId)).size !== value.sources.length) return 'invalid';
    return value;
  } catch { return 'invalid'; }
}

/** Admission is supplied by the ordinary decision plan. This helper never
 * infers acceptance from source IDs, title, dates, report scores or prose. */
export function customBriefSourcesAreCurrent(report: SiteRagReport, siteId: string,
  acceptedCurrentReports: readonly SiteRagReport[]): boolean {
  const frame = customBriefSourceFrame(report);
  // Existing unstamped reports retain their old SED/config currency policy.
  // New generation always stamps even an empty frame, so it cannot use this.
  if (frame === null) return true;
  if (frame === 'invalid' || frame.siteId !== siteId) return false;
  const actual = acceptedCurrentReports.filter(source => source.siteId === siteId
    && source.reportType !== 'custom-brief' && source.reportType !== 'executive-summary'
    && customBriefReportHasSourceBody(source));
  if (actual.length !== frame.sources.length) return false;
  const receipts = new Map(actual.map(source => [source.id, customBriefReportSourceReceipt(source)]));
  return frame.sources.every(expected => {
    const current = receipts.get(expected.reportId);
    return Boolean(current && hashCanonicalJson(current) === hashCanonicalJson(expected));
  });
}
